import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, ArrowRight, User, Loader2, Sparkles, Building2, Languages, FileText, Paperclip, X, File, Clock, CheckCheck, PlusCircle, ClipboardList, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import VoiceRecorder from '../components/VoiceRecorder';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../config/theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AI_AVATAR = "/cloudgm-ai-orb.png";

// Service configurations
const SERVICE_CONFIGS = {
  company_formation: {
    title: 'مساعد تأسيس الشركات',
    titleEn: 'Company Formation Assistant',
    welcomeMessage: `مرحباً بك في CloudGM AI! ✨

أنا مساعدك الافتراضي المتخصص في تأسيس الشركات في رأس الخيمة.

سأساعدك في رحلة تأسيس شركتك بأسرع وقت وأقل تكلفة.

للبدء، أخبرني: ما نوع النشاط التجاري الذي ترغب في تأسيسه؟`,
    icon: <Building2 className="h-5 w-5" />,
    backUrl: '/services/company-formation',
    apiEndpoint: '/api/ai/chat/company-formation',
    suggestions: ['كم تكلفة تأسيس شركة استشارات؟', 'أبغى رخصة فريلانسر', 'شو الأنشطة التجارية المتاحة؟']
  },
  translation_services: {
    title: 'مساعد خدمات الترجمة',
    titleEn: 'Translation Services Assistant',
    welcomeMessage: `مرحباً بك في CloudGM AI! ✨

أنا مساعدك الافتراضي لخدمات الترجمة القانونية.

سأساعدك في جمع جميع المعلومات المطلوبة لخدمات الترجمة وإرسالها لفريقنا المتخصص.

للبدء، أخبرني:
1. ما نوع المستند الذي تريد ترجمته؟
2. من أي لغة إلى أي لغة؟`,
    icon: <Languages className="h-5 w-5" />,
    backUrl: '/services',
    apiEndpoint: '/api/ai/chat/services',
    suggestions: ['ترجمة شهادة ميلاد', 'ترجمة عقد قانوني', 'كم مدة الترجمة المعتمدة؟']
  },
  document_clearing: {
    title: 'مساعد تخليص المستندات',
    titleEn: 'Document Clearing Assistant',
    welcomeMessage: `مرحباً بك في CloudGM AI! ✨

أنا مساعدك الافتراضي لخدمات الطباعة وتخليص المستندات.

سأساعدك في جمع جميع المعلومات المطلوبة وإرسالها لفريقنا لتخليص معاملتك.

للبدء، أخبرني: ما نوع المعاملة أو المستند الذي تحتاج لتخليصه؟
- تجديد إقامة
- هوية إماراتية
- رخصة قيادة
- معاملات أخرى`,
    icon: <FileText className="h-5 w-5" />,
    backUrl: '/services',
    apiEndpoint: '/api/ai/chat/services',
    suggestions: ['تجديد إقامة', 'إصدار هوية إماراتية', 'تجديد رخصة قيادة']
  }
};

export default function CompanyFormationChat() {
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `cf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [requestCompleted, setRequestCompleted] = useState(false);
  const [requestNumber, setRequestNumber] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [timeoutWarning, setTimeoutWarning] = useState(false);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();

  // (Voice recording now handled by shared VoiceRecorder using Whisper)

  // Check if speech recognition is supported
  useEffect(() => {
    // No-op: speech recognition replaced by server-side Whisper (VoiceRecorder component)
  }, []);

  // Toggle voice recording — handled by <VoiceRecorder />.
  // Auto-send the transcribed text as a chat message (WhatsApp-style flow).
  const handleVoiceTranscript = (text) => {
    if (!text || !text.trim()) return;
    sendMessage(null, text);
  };

  // Get service type and info from navigation state
  const serviceContext = location.state?.serviceContext || 'company_formation';
  const serviceInfo = location.state?.serviceInfo || null;
  const selectedService = location.state?.selectedService || null;
  const config = SERVICE_CONFIGS[serviceContext] || SERVICE_CONFIGS.company_formation;

  // Reset timeout on activity
  const resetTimeout = () => {
    setTimeoutWarning(false);
    
    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    
    // Warning after 4 minutes
    warningTimeoutRef.current = setTimeout(() => {
      if (!requestCompleted && !sessionClosed) {
        setTimeoutWarning(true);
        toast.warning('ستُغلق المحادثة خلال دقيقة واحدة بسبب عدم النشاط');
      }
    }, 4 * 60 * 1000); // 4 minutes
    
    // Close session after 5 minutes
    timeoutRef.current = setTimeout(() => {
      if (!requestCompleted && !sessionClosed) {
        handleSessionTimeout();
      }
    }, 5 * 60 * 1000); // 5 minutes
  };

  // Handle session timeout
  const handleSessionTimeout = async () => {
    setSessionClosed(true);
    toast.info('تم إغلاق المحادثة بسبب عدم النشاط');
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/chat/close-session`,
        {
          session_id: sessionId,
          reason: 'timeout',
          messages: messages
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error closing session:', error);
    }
  };

  // Start new conversation
  const startNewConversation = () => {
    setMessages([]);
    setRequestCompleted(false);
    setRequestNumber(null);
    setSessionClosed(false);
    setTimeoutWarning(false);
    setAttachments([]);
    
    const personalizedWelcome = config.welcomeMessage.replace(
      'مرحباً بك',
      `مرحباً ${user?.name || 'بك'}`
    );
    
    setMessages([{
      role: 'assistant',
      content: personalizedWelcome
    }]);
    
    resetTimeout();
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!userData || !token) {
      toast.error('يجب تسجيل الدخول أولاً');
      navigate('/login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    // Personalized welcome message
    const personalizedWelcome = config.welcomeMessage.replace(
      'مرحباً بك',
      `مرحباً ${parsedUser.name || 'بك'}`
    );
    
    setMessages([{
      role: 'assistant',
      content: personalizedWelcome
    }]);
    
    // Start timeout
    resetTimeout();
    
    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    };
  }, [navigate, config.welcomeMessage]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // File upload handler
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingFile(true);
    
    for (const file of files) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`الملف ${file.name} كبير جداً. الحد الأقصى 5MB`);
        continue;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`نوع الملف ${file.name} غير مدعوم. يرجى رفع صور أو PDF`);
        continue;
      }
      
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('session_id', sessionId);
        
        const response = await axios.post(
          `${BACKEND_URL}/api/upload/chat-attachment`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );
        
        if (response.data.success) {
          setAttachments(prev => [...prev, {
            id: response.data.file_id,
            name: file.name,
            type: file.type,
            url: response.data.url,
            size: file.size
          }]);
          toast.success(`تم رفع ${file.name} بنجاح`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`فشل رفع ${file.name}`);
      }
    }
    
    setUploadingFile(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const sendMessage = async (e, presetText = null) => {
    e?.preventDefault();
    
    const textToSend = presetText !== null ? presetText : inputMessage;
    if ((!textToSend.trim() && attachments.length === 0) || loading || requestCompleted || sessionClosed) return;
    
    // Reset timeout on activity
    resetTimeout();
    
    const userMessage = textToSend.trim();
    const currentAttachments = [...attachments];
    setInputMessage('');
    setAttachments([]);
    
    setMessages(prev => [...prev, { role: 'user', content: userMessage, attachments: currentAttachments }]);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('يجب تسجيل الدخول أولاً');
        navigate('/login');
        return;
      }
      
      const response = await axios.post(
        `${BACKEND_URL}${config.apiEndpoint}`,
        {
          message: userMessage || 'تم إرفاق ملفات',
          session_id: sessionId,
          service_type: serviceContext,
          service_info: serviceInfo,
          attachments: currentAttachments
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      
      // Check if request was submitted
      if (response.data.request_submitted && response.data.request_number) {
        setRequestCompleted(true);
        setRequestNumber(response.data.request_number);
        toast.success(`تم تسجيل طلبك بنجاح! رقم الطلب: ${response.data.request_number}`);
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('انتهت جلستك، يرجى تسجيل الدخول مرة أخرى');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      
      toast.error('حدث خطأ في المحادثة');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.' 
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Theme-aware helper tokens
  const surface = isDarkMode ? 'rgba(20,20,20,0.75)' : 'rgba(255,255,255,0.8)';
  const aiBubbleBg = isDarkMode ? 'rgba(26,26,26,0.9)' : '#ffffff';

  if (!user) {
    return (
      <div className="fixed inset-0 bg-theme-primary flex items-center justify-center z-50" data-testid="chat-loading-screen">
        <div className="flex flex-col items-center gap-5">
          <img src={AI_AVATAR} alt="CloudGM AI" className="w-24 h-24 animate-float" style={{ filter: 'drop-shadow(0 0 30px rgba(30,155,217,0.6))' }} />
          <p className="text-theme-secondary text-sm tracking-widest">CLOUDGM AI</p>
        </div>
      </div>
    );
  }

  const showWelcomeHero = messages.length <= 1 && !loading;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-theme-primary text-theme-primary overflow-hidden" data-testid="ai-chat-page">
      {/* Ambient AI background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 start-1/4 w-[500px] h-[500px] rounded-full opacity-25"
          style={{ background: 'radial-gradient(circle, rgba(30,155,217,0.45) 0%, transparent 65%)' }} />
        <div className="absolute bottom-0 end-0 w-[420px] h-[420px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.35) 0%, transparent 65%)' }} />
        <div className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.05)'} 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }} />
      </div>

      {/* Header */}
      <header className="relative z-20 flex-shrink-0 glass-effect border-b border-theme" data-testid="ai-chat-header">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <button 
                onClick={() => navigate(config.backUrl)}
                className="p-2.5 rounded-full border border-theme text-theme-secondary hover:text-theme-primary hover:border-[#1E9BD9]/50 transition-colors"
                data-testid="chat-back-button"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
              
              <div className="flex items-center gap-3">
                {/* AI Avatar */}
                <div className="relative">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: 'radial-gradient(circle, rgba(30,155,217,0.18) 0%, transparent 70%)' }}>
                    <img src={AI_AVATAR} alt="CloudGM AI" className="w-11 h-11 object-contain"
                      style={{ filter: 'drop-shadow(0 0 12px rgba(30,155,217,0.55))' }} />
                  </div>
                  <span className="absolute inset-0 rounded-full border border-[#1E9BD9]/40 animate-ping" style={{ animationDuration: '2.5s' }}></span>
                  <div className="absolute -bottom-0.5 -left-0.5 w-3.5 h-3.5 bg-[#10B981] rounded-full border-2" style={{ borderColor: 'var(--bg-primary)' }}></div>
                </div>
                
                <div>
                  <h1 className="font-bold text-lg leading-tight gradient-text" data-testid="chat-bot-name">CLOUDGM AI</h1>
                  <p className="text-xs text-theme-secondary flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-pulse"></span>
                    {config.title}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#10B981] px-4 py-2 rounded-full border border-[#10B981]/30 bg-[#10B981]/10" data-testid="chat-online-status">
              <span className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></span>
              متصل
            </div>
          </div>
        </div>
      </header>

      {/* Chat Messages */}
      <div className="relative z-10 flex-1 overflow-y-auto" data-testid="chat-messages-area">
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-7">

          {/* Welcome hero */}
          {showWelcomeHero && (
            <div className="flex flex-col items-center text-center pt-4 pb-2 animate-fade-in-up" data-testid="chat-welcome-hero">
              <div className="relative mb-2">
                <img src={AI_AVATAR} alt="CloudGM AI" className="w-28 h-28 sm:w-32 sm:h-32 object-contain animate-float"
                  style={{ filter: 'drop-shadow(0 0 45px rgba(30,155,217,0.65))' }} />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold gradient-text mb-1">CloudGM AI</h2>
              <p className="text-theme-secondary text-sm">{config.title} — CLOUDGM AI</p>
            </div>
          )}

          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              style={{ 
                animation: 'fadeSlideIn 0.35s ease-out forwards',
                animationDelay: `${Math.min(index, 4) * 0.05}s`,
                opacity: 0
              }}
              data-testid={`chat-msg-${msg.role}-${index}`}
            >
              {/* Avatar */}
              {msg.role === 'assistant' ? (
                <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: 'radial-gradient(circle, rgba(30,155,217,0.15) 0%, transparent 70%)' }}>
                  <img src={AI_AVATAR} alt="CloudGM AI" className="w-9 h-9 object-contain"
                    style={{ filter: 'drop-shadow(0 0 8px rgba(30,155,217,0.5))' }} />
                </div>
              ) : (
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#1E9BD9] to-[#0284C7] flex items-center justify-center shadow-[0_0_18px_rgba(30,155,217,0.35)]">
                  <User className="h-5 w-5 text-white" />
                </div>
              )}
              
              {/* Message Bubble */}
              <div className={`max-w-[82%] ${msg.role === 'user' ? 'text-end' : ''}`}>
                <div className={`inline-block px-5 py-3.5 text-start ${
                  msg.role === 'user'
                    ? 'rounded-2xl rounded-te-md text-white bg-gradient-to-br from-[#1E9BD9] to-[#0284C7] shadow-[0_6px_24px_rgba(30,155,217,0.35)]'
                    : 'rounded-2xl rounded-ts-md border border-theme text-theme-primary backdrop-blur-xl'
                }`}
                style={msg.role === 'assistant' ? { background: aiBubbleBg } : {}}>
                  {/* Show attachments if any */}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {msg.attachments.map((att, attIdx) => (
                        <div key={attIdx} className={`rounded-xl overflow-hidden ${msg.role === 'user' ? 'bg-white/20' : 'bg-theme-surface'}`}>
                          {att.type?.startsWith('image/') ? (
                            <a href={att.url} target="_blank" rel="noopener noreferrer">
                              <img src={att.url} alt={att.name} className="max-w-[200px] max-h-[150px] object-cover rounded-xl" />
                            </a>
                          ) : (
                            <a 
                              href={att.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 px-3 py-2 ${msg.role === 'user' ? 'text-white' : 'text-[#1E9BD9]'}`}
                            >
                              <File className="h-4 w-4" />
                              <span className="text-sm truncate max-w-[150px]">{att.name}</span>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {msg.content && <p className="whitespace-pre-wrap leading-relaxed text-[15px]">{msg.content}</p>}
                </div>
                <p className={`text-[11px] text-theme-secondary mt-1.5 px-1 flex items-center gap-1 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                  {msg.role === 'assistant' ? (
                    <><Sparkles className="h-3 w-3 text-[#1E9BD9]" /> CLOUDGM AI</>
                  ) : (
                    <><CheckCheck className="h-3 w-3 text-[#1E9BD9]" /> {user?.name || 'أنت'}</>
                  )}
                </p>
              </div>
            </div>
          ))}
          
          {/* Thinking indicator */}
          {loading && (
            <div className="flex gap-3" style={{ animation: 'fadeSlideIn 0.3s ease-out forwards' }} data-testid="chat-typing-indicator">
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'radial-gradient(circle, rgba(30,155,217,0.15) 0%, transparent 70%)' }}>
                <img src={AI_AVATAR} alt="CloudGM AI" className="w-9 h-9 object-contain animate-pulse"
                  style={{ filter: 'drop-shadow(0 0 10px rgba(30,155,217,0.6))' }} />
              </div>
              <div className="px-5 py-4 rounded-2xl rounded-ts-md border border-theme backdrop-blur-xl" style={{ background: aiBubbleBg }}>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-[#1E9BD9] rounded-full" style={{ animation: 'aiDot 1.2s ease-in-out infinite', boxShadow: '0 0 8px rgba(30,155,217,0.8)' }}></span>
                    <span className="w-2 h-2 bg-[#1E9BD9] rounded-full" style={{ animation: 'aiDot 1.2s ease-in-out 0.15s infinite', boxShadow: '0 0 8px rgba(30,155,217,0.8)' }}></span>
                    <span className="w-2 h-2 bg-[#1E9BD9] rounded-full" style={{ animation: 'aiDot 1.2s ease-in-out 0.3s infinite', boxShadow: '0 0 8px rgba(30,155,217,0.8)' }}></span>
                  </div>
                  <span className="text-sm text-theme-secondary">يفكّر...</span>
                </div>
              </div>
            </div>
          )}

          {/* Suggestion chips */}
          {showWelcomeHero && config.suggestions && (
            <div className="flex flex-wrap justify-center gap-2.5 pt-1 animate-fade-in-up" data-testid="chat-suggestions">
              {config.suggestions.map((sug, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(null, sug)}
                  className="px-4 py-2.5 rounded-full text-sm border border-[#1E9BD9]/35 text-[#1E9BD9] bg-[#1E9BD9]/5 hover:bg-[#1E9BD9]/15 hover:border-[#1E9BD9]/60 transition-colors backdrop-blur-md"
                  data-testid={`chat-suggestion-${i}`}
                >
                  {sug}
                </button>
              ))}
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="relative z-20 flex-shrink-0 glass-effect border-t border-theme p-4">
        {/* Timeout Warning */}
        {timeoutWarning && !requestCompleted && !sessionClosed && (
          <div className="max-w-4xl mx-auto mb-3">
            <div className="rounded-2xl p-3 flex items-center gap-3 border border-yellow-500/30 bg-yellow-500/10" data-testid="chat-timeout-warning">
              <Clock className="h-5 w-5 text-yellow-500 animate-pulse" />
              <p className="text-sm text-yellow-600 dark:text-yellow-400">ستُغلق المحادثة خلال دقيقة واحدة بسبب عدم النشاط</p>
            </div>
          </div>
        )}
        
        {/* Session Closed */}
        {sessionClosed && !requestCompleted ? (
          <div className="max-w-4xl mx-auto">
            <div className="rounded-3xl p-8 border border-theme text-center backdrop-blur-xl" style={{ background: surface }} data-testid="chat-session-closed">
              <div className="w-20 h-20 rounded-full border border-theme flex items-center justify-center mx-auto mb-4 bg-theme-surface">
                <Clock className="w-9 h-9 text-theme-secondary" />
              </div>
              <h3 className="text-xl font-bold text-theme-primary mb-2">انتهت المحادثة</h3>
              <p className="text-theme-secondary mb-6">تم إغلاق المحادثة بسبب عدم النشاط لمدة 5 دقائق</p>
              
              <Button 
                onClick={startNewConversation}
                className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] text-white px-8 py-3 rounded-full shadow-[0_6px_24px_rgba(30,155,217,0.35)]"
                data-testid="chat-restart-button"
              >
                بدء محادثة جديدة
              </Button>
            </div>
          </div>
        ) : requestCompleted ? (
          /* Request Completed */
          <div className="max-w-4xl mx-auto">
            {/* Success Card */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl p-6 text-white shadow-[0_10px_40px_rgba(16,185,129,0.35)] mb-4" data-testid="chat-success-card">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center flex-shrink-0">
                  <CheckCheck className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-1">تم إرسال طلبك بنجاح!</h3>
                  <p className="text-white/80 text-sm mb-3">سيتم مراجعة طلبك من قبل فريقنا وسنتواصل معك قريباً</p>
                  <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-2 inline-block">
                    <span className="text-white/70 text-sm">رقم الطلب:</span>
                    <span className="font-bold text-lg ms-2" data-testid="chat-request-number">{requestNumber}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Button 
                onClick={() => navigate('/dashboard', { state: { activeTab: 'requests' } })}
                className="bg-[#1E9BD9] hover:bg-[#1789c2] text-white py-4 rounded-full flex items-center justify-center gap-2"
                data-testid="chat-track-request-btn"
              >
                <ClipboardList className="w-5 h-5" />
                تتبع طلبي
              </Button>
              <Button 
                onClick={startNewConversation}
                variant="outline"
                className="py-4 rounded-full border-2 border-[#1E9BD9] text-[#1E9BD9] hover:bg-[#1E9BD9]/10 flex items-center justify-center gap-2 bg-transparent"
                data-testid="chat-new-request-btn"
              >
                <PlusCircle className="w-5 h-5" />
                طلب جديد
              </Button>
            </div>
            
            {/* Other Services */}
            <div className="rounded-2xl p-4 border border-theme backdrop-blur-xl" style={{ background: surface }}>
              <h4 className="text-sm font-bold text-theme-primary mb-3">استكشف خدماتنا الأخرى</h4>
              <div className="grid grid-cols-4 gap-2">
                <button 
                  onClick={() => navigate('/services/company-formation')}
                  className="flex flex-col items-center gap-1 p-2 rounded-xl bg-theme-card border border-theme hover:border-green-400/50 transition-colors"
                >
                  <div className="w-10 h-10 bg-green-500/15 rounded-xl flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-green-500" />
                  </div>
                  <span className="text-[10px] text-theme-secondary text-center">تأسيس شركات</span>
                </button>
                <button 
                  onClick={() => navigate('/services/translation')}
                  className="flex flex-col items-center gap-1 p-2 rounded-xl bg-theme-card border border-theme hover:border-blue-400/50 transition-colors"
                >
                  <div className="w-10 h-10 bg-blue-500/15 rounded-xl flex items-center justify-center">
                    <Languages className="h-5 w-5 text-blue-500" />
                  </div>
                  <span className="text-[10px] text-theme-secondary text-center">ترجمة قانونية</span>
                </button>
                <button 
                  onClick={() => navigate('/services/documents')}
                  className="flex flex-col items-center gap-1 p-2 rounded-xl bg-theme-card border border-theme hover:border-orange-400/50 transition-colors"
                >
                  <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center">
                    <FileText className="h-5 w-5 text-orange-500" />
                  </div>
                  <span className="text-[10px] text-theme-secondary text-center">تخليص مستندات</span>
                </button>
                <button 
                  onClick={() => navigate('/products')}
                  className="flex flex-col items-center gap-1 p-2 rounded-xl bg-theme-card border border-theme hover:border-pink-400/50 transition-colors"
                >
                  <div className="w-10 h-10 bg-pink-500/15 rounded-xl flex items-center justify-center">
                    <ShoppingBag className="h-5 w-5 text-pink-500" />
                  </div>
                  <span className="text-[10px] text-theme-secondary text-center">المتجر</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Normal Input Form */
          <form onSubmit={sendMessage} className="max-w-4xl mx-auto">
            {/* Attachments Preview */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3 p-3 rounded-2xl border border-theme bg-theme-surface" data-testid="chat-attachments-preview">
                {attachments.map((att) => (
                  <div key={att.id} className="relative group">
                    {att.type?.startsWith('image/') ? (
                      <div className="relative">
                        <img src={att.url} alt={att.name} className="h-16 w-16 object-cover rounded-xl border border-theme" />
                        <button
                          type="button"
                          onClick={() => removeAttachment(att.id)}
                          className="absolute -top-2 -end-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative flex items-center gap-2 px-3 py-2 bg-theme-card rounded-xl border border-theme">
                        <File className="h-4 w-4 text-[#1E9BD9]" />
                        <span className="text-sm text-theme-secondary truncate max-w-[100px]">{att.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(att.id)}
                          className="w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Pill-shaped interaction zone */}
            <div className={`flex items-center gap-2 rounded-full border px-2.5 py-2 transition-colors backdrop-blur-xl border-theme focus-within:border-[#1E9BD9]/60 focus-within:ring-2 focus-within:ring-[#1E9BD9]/20`} style={{ background: surface }} data-testid="chat-input-zone">
              {/* File Upload Button */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="chat-file-input"
              />
              {!isVoiceRecording && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || uploadingFile}
                  className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-theme-secondary hover:text-[#1E9BD9] hover:bg-[#1E9BD9]/10 transition-colors disabled:opacity-40"
                  data-testid="chat-attach-button"
                >
                  {uploadingFile ? (
                    <Loader2 className="h-5 w-5 animate-spin text-[#1E9BD9]" />
                  ) : (
                    <Paperclip className="h-5 w-5" />
                  )}
                </button>
              )}

              {/* Voice Recording — expands to full-width bar while recording */}
              <VoiceRecorder
                onTranscript={handleVoiceTranscript}
                onRecordingChange={setIsVoiceRecording}
                language="ar"
                disabled={loading}
                testId="voice-recording-btn"
              />

              {!isVoiceRecording && (
                <>
                  <input
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="اسأل CloudGM AI أي شيء..."
                    disabled={loading}
                    className="flex-1 bg-transparent border-none outline-none text-base text-theme-primary placeholder:text-theme-secondary px-2 py-2.5"
                    data-testid="chat-input"
                  />

                  <button
                    type="submit"
                    disabled={loading || (!inputMessage.trim() && attachments.length === 0)}
                    className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#1E9BD9] to-[#0284C7] text-white flex items-center justify-center shadow-[0_4px_20px_rgba(30,155,217,0.45)] hover:scale-105 transition-transform disabled:opacity-40 disabled:scale-100"
                    data-testid="chat-send-button"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 -scale-x-100" />}
                  </button>
                </>
              )}
            </div>
            <p className="text-center text-xs text-theme-secondary mt-3 flex items-center justify-center gap-2">
              <Sparkles className="h-3 w-3 text-[#1E9BD9]" />
              CLOUDGM AI — يمكنك إرفاق ملفات أو التحدث بالصوت
            </p>
          </form>
        )}
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { 
            opacity: 0; 
            transform: translateY(10px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        @keyframes aiDot {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
